import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from '@tanstack/react-router';
import { AlertCircle, Loader2, MailCheck } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { PublicPageShell } from '@/components/auth/PublicPageShell';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { requestPasswordRecovery } from '../account-access.api';
import { ForgotFormValues, forgotSchema } from '../account-access.schemas';

/** `/forgot`: mails a recovery link. The answer never reveals whether the account exists. */
export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<ForgotFormValues>({
    resolver: zodResolver(forgotSchema),
    defaultValues: { identifier: '' },
  });

  const onSubmit = async (values: ForgotFormValues) => {
    setError(null);
    try {
      await requestPasswordRecovery(values.identifier);
      setSent(true);
    } catch {
      setError('Something went wrong. Please try again later.');
    }
  };

  const { isSubmitting } = form.formState;

  return (
    <PublicPageShell
      title="Forgot password"
      description="Enter your username or email address and we will send you a link to choose a new password"
    >
      {sent ? (
        <div className="space-y-4">
          <Alert>
            <MailCheck className="h-4 w-4" />
            <AlertTitle>Check your inbox</AlertTitle>
            <AlertDescription>
              If an active account matches, a recovery link has been sent to its
              email address. The link is valid for 24 hours.
            </AlertDescription>
          </Alert>
          <Button asChild tooltip={false} variant="outline" className="w-full">
            <Link to="/login">Back to sign in</Link>
          </Button>
        </div>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <FormField
              control={form.control}
              name="identifier"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Username or email</FormLabel>
                  <FormControl>
                    <Input autoComplete="username" autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="animate-spin" />}
              Send recovery link
            </Button>
            <p className="text-muted-foreground text-center text-sm">
              <Link to="/login" className="underline">
                Back to sign in
              </Link>
            </p>
          </form>
        </Form>
      )}
    </PublicPageShell>
  );
}
